/**
 * CompanyDocumentsSection — single-screen hub for company docs (Trade License + Tax Certificate).
 *
 * The flow:
 *   1) Hub view: 2 big buttons (الرخصة التجارية / شهادة الضريبة) showing counts.
 *   2) Detail view per doc_type: list of uploaded docs + an "Upload new" CTA.
 *   3) Upload modal: drag/drop a PDF or image → "استخراج بالذكاء الاصطناعي" → preview extracted
 *      fields → "حفظ".
 *
 * AI OCR is done by POST /api/admin/company-docs/extract (Gemini Vision).
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  FileText, ShieldCheck, Upload, Loader2, Plus, Trash2, Eye, ArrowRight, ArrowLeft,
  Calendar, AlertCircle, FileCheck2, FileImage, Sparkles, X,
  KeyRound, Landmark, AlertTriangle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { getAuthHeaders, getFileUrl } from '../../../utils/authHeaders';
import CompanySettingsSection from './CompanySettingsSection';
import BankAccountsSection from './BankAccountsSection';
import ExpiryWatchSection from './ExpiryWatchSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = {
  trade_license: {
    titleAr: 'الرخصة التجارية',
    titleEn: 'Trade License',
    icon: FileCheck2,
    color: '#d4af37',
    bg: 'bg-[#d4af37]/10',
    border: 'border-[#d4af37]/30',
    descAr: 'ارفع نسخة الرخصة (PDF أو صورة) — يقرأها الذكاء الاصطناعي تلقائياً.',
    descEn: 'Upload your trade license (PDF or image) — AI auto-reads it.',
    fields: [
      { key: 'license_number', labelAr: 'رقم الرخصة', labelEn: 'License No.' },
      { key: 'trade_name_ar', labelAr: 'الاسم التجاري (عربي)', labelEn: 'Trade Name (AR)' },
      { key: 'trade_name_en', labelAr: 'الاسم التجاري (إنجليزي)', labelEn: 'Trade Name (EN)' },
      { key: 'legal_type_ar', labelAr: 'الشكل القانوني', labelEn: 'Legal Type (AR)' },
      { key: 'legal_type_en', labelAr: 'Legal Type', labelEn: 'Legal Type (EN)' },
      { key: 'issuing_authority', labelAr: 'الجهة المصدرة', labelEn: 'Issuing Authority' },
      { key: 'issue_date', labelAr: 'تاريخ الإصدار', labelEn: 'Issue Date' },
      { key: 'expiry_date', labelAr: 'تاريخ الانتهاء', labelEn: 'Expiry Date', highlight: true },
      { key: 'main_address', labelAr: 'العنوان', labelEn: 'Address' },
      { key: 'phone', labelAr: 'الهاتف', labelEn: 'Phone' },
      { key: 'email', labelAr: 'البريد', labelEn: 'Email' },
    ],
  },
  tax_certificate: {
    titleAr: 'شهادة الضريبة',
    titleEn: 'Tax Certificate',
    icon: ShieldCheck,
    color: '#10B981',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    descAr: 'ارفع شهادة TRN / VAT / Corporate Tax (PDF أو صورة).',
    descEn: 'Upload TRN / VAT / Corporate Tax certificate (PDF or image).',
    fields: [
      { key: 'tax_type', labelAr: 'نوع الضريبة', labelEn: 'Tax Type' },
      { key: 'trn_number', labelAr: 'رقم TRN', labelEn: 'TRN Number' },
      { key: 'legal_name_ar', labelAr: 'الاسم القانوني (عربي)', labelEn: 'Legal Name (AR)' },
      { key: 'legal_name_en', labelAr: 'الاسم القانوني (إنجليزي)', labelEn: 'Legal Name (EN)' },
      { key: 'tax_registration_date', labelAr: 'تاريخ التسجيل', labelEn: 'Registration Date' },
      { key: 'effective_date', labelAr: 'تاريخ السريان', labelEn: 'Effective Date' },
      { key: 'issue_date', labelAr: 'تاريخ الإصدار', labelEn: 'Issue Date' },
      { key: 'expiry_date', labelAr: 'تاريخ الانتهاء', labelEn: 'Expiry Date', highlight: true },
      { key: 'address', labelAr: 'العنوان', labelEn: 'Address' },
    ],
  },
};

// ---------- helpers ----------
function daysUntil(ddmmyyyy) {
  if (!ddmmyyyy || typeof ddmmyyyy !== 'string') return null;
  const m = ddmmyyyy.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!m) return null;
  const [, d, mo, y] = m;
  const target = new Date(Number(y), Number(mo) - 1, Number(d));
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

function ExpiryBadge({ ddmmyyyy, language }) {
  const days = daysUntil(ddmmyyyy);
  if (days == null) return null;
  let cls = 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
  let label = language === 'ar' ? `${days} يوم متبقي` : `${days} days left`;
  if (days < 0) {
    cls = 'bg-red-500/10 text-red-400 border-red-500/30';
    label = language === 'ar' ? `منتهية منذ ${Math.abs(days)} يوم` : `expired ${Math.abs(days)} d ago`;
  } else if (days <= 30) {
    cls = 'bg-amber-500/10 text-amber-400 border-amber-500/30';
  }
  return (
    <span
      data-testid={days < 0 ? 'doc-expired-badge' : days <= 30 ? 'doc-expiring-badge' : 'doc-valid-badge'}
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-full border ${cls}`}
    >
      <Calendar className="h-3 w-3" />
      {label}
    </span>
  );
}

// ====================================================================
// Settings/Watch tiles (rendered after AI-doc tiles in the hub)
const EXTRA_TILES = [
  {
    id: 'partner-code',
    titleAr: 'كود الشريك (RAKEZ)',
    titleEn: 'Partner Code (RAKEZ)',
    descAr: 'كود الشركة المعتمد لدى منطقة راكز الحرة، يظهر على الفواتير الضريبية.',
    descEn: 'Your RAKEZ-approved partner code, printed on tax invoices.',
    icon: KeyRound,
    color: '#60A5FA',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
  },
  {
    id: 'bank-account',
    titleAr: 'الحسابات المالية للشركة',
    titleEn: 'Company Bank Accounts',
    descAr: 'حسابات الشركة البنكية — أضف حساباً برفع رسالة الايبان ويُقرأ تلقائياً بالذكاء الاصطناعي.',
    descEn: 'Company bank accounts — add one by uploading the IBAN letter (auto-read by AI).',
    icon: Landmark,
    color: '#A78BFA',
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/30',
  },
  {
    id: 'expiry-watch',
    titleAr: 'مراقبة الصلاحيات',
    titleEn: 'Expiry Watch',
    descAr: 'تتبع تواريخ انتهاء وثائق الشركة وبطاقات الموظفين، وأرسل تذكيرات.',
    descEn: 'Track expiry of company docs + employee IDs, send reminders.',
    icon: AlertTriangle,
    color: '#F59E0B',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
  },
];

// ====================================================================
export default function CompanyDocumentsSection({ language, backRef }) {
  const [activeDocType, setActiveDocType] = useState(null); // null = hub, or trade_license/tax_certificate/partner-code/bank-account/expiry-watch
  const [counts, setCounts] = useState({ trade_license: 0, tax_certificate: 0 });
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  // iter-249 — Manual TRN override (used on all invoices/receipts).
  const [manualTrn, setManualTrn] = useState('');
  const [effectiveTrn, setEffectiveTrn] = useState('');
  const [savingTrn, setSavingTrn] = useState(false);

  // Load counts for hub badges
  const fetchCounts = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [a, b] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/company-docs?doc_type=trade_license`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/company-docs?doc_type=tax_certificate`, { headers }),
      ]);
      setCounts({
        trade_license: a.data?.count || 0,
        tax_certificate: b.data?.count || 0,
      });
    } catch (err) {
      // silent — hub still renders
    }
  }, []);

  // Load docs for the active type
  const fetchDocs = useCallback(async (type) => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${BACKEND_URL}/api/admin/company-docs?doc_type=${type}`, { headers });
      setDocs(res.data?.data || []);
      // iter-277 — keep the "Active TRN" chip in sync after every fetch
      // of the tax-certificate view (post-upload, post-delete, back-nav).
      if (type === 'tax_certificate') {
        try {
          const s = await axios.get(`${BACKEND_URL}/api/admin/company-settings`, { headers });
          setEffectiveTrn(s.data?.company_info?.trn || '');
        } catch (_) { /* non-fatal */ }
      }
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل تحميل الوثائق' : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  // Register the internal back handler: when inside a sub-view, the page-level
  // back button returns to the Company Documents hub instead of leaving the page.
  useEffect(() => {
    if (!backRef) return undefined;
    backRef.current = () => {
      if (activeDocType) {
        setActiveDocType(null);
        fetchCounts();
        return true; // handled internally
      }
      return false;
    };
    return () => { backRef.current = null; };
  }, [backRef, activeDocType, fetchCounts]);

  useEffect(() => {
    // Only fetch documents for AI-doc types
    if (activeDocType === 'trade_license' || activeDocType === 'tax_certificate') {
      fetchDocs(activeDocType);
    }
    // iter-249 — Load the manual TRN whenever the tax certificate view opens
    if (activeDocType === 'tax_certificate') {
      (async () => {
        try {
          const r = await axios.get(`${BACKEND_URL}/api/admin/company-settings`, { headers: getAuthHeaders() });
          setManualTrn(r.data?.manual_trn || '');
          setEffectiveTrn(r.data?.company_info?.trn || '');
        } catch {
          // silent
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDocType]);

  // ---------- Hub view ----------
  if (!activeDocType) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
        data-testid="company-docs-hub"
      >
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6">
          <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#d4af37]" />
            {language === 'ar' ? 'وثائق الشركة' : 'Company Documents'}
          </h2>
          <p className="text-sm text-[#A1A1AA] mb-6">
            {language === 'ar'
              ? 'إدارة وثائق شركتك الرسمية. يقرأ الذكاء الاصطناعي الملف تلقائياً ويستخرج البيانات وتاريخ الانتهاء.'
              : 'Manage your company\'s official documents. AI auto-reads the file and extracts data + expiry.'}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(DOC_TYPES).map(([type, meta]) => {
              const Icon = meta.icon;
              return (
                <button
                  key={type}
                  onClick={() => setActiveDocType(type)}
                  data-testid={`open-${type}`}
                  className={`group text-start p-6 rounded-2xl border-2 transition-all ${meta.bg} ${meta.border} hover:scale-[1.02] hover:border-opacity-80`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0`} style={{ backgroundColor: `${meta.color}22` }}>
                      <Icon className="h-7 w-7" style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-white">
                          {language === 'ar' ? meta.titleAr : meta.titleEn}
                        </h3>
                        <span className="px-2 py-0.5 text-[10px] rounded-full" style={{ backgroundColor: `${meta.color}22`, color: meta.color }}>
                          {counts[type] || 0}
                        </span>
                      </div>
                      <p className="text-sm text-[#A1A1AA]">{language === 'ar' ? meta.descAr : meta.descEn}</p>
                    </div>
                    <ArrowLeft className={`h-5 w-5 transition-all opacity-50 group-hover:opacity-100 ${language === 'ar' ? '' : 'rotate-180'}`} style={{ color: meta.color }} />
                  </div>
                </button>
              );
            })}

            {/* === Extra tiles: Partner Code / Bank Account / Expiry Watch === */}
            {EXTRA_TILES.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveDocType(t.id)}
                  data-testid={`open-${t.id}`}
                  className={`group text-start p-6 rounded-2xl border-2 transition-all ${t.bg} ${t.border} hover:scale-[1.02] hover:border-opacity-80`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${t.color}22` }}>
                      <Icon className="h-7 w-7" style={{ color: t.color }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white mb-1">
                        {language === 'ar' ? t.titleAr : t.titleEn}
                      </h3>
                      <p className="text-sm text-[#A1A1AA]">{language === 'ar' ? t.descAr : t.descEn}</p>
                    </div>
                    <ArrowLeft className={`h-5 w-5 transition-all opacity-50 group-hover:opacity-100 ${language === 'ar' ? '' : 'rotate-180'}`} style={{ color: t.color }} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </motion.div>
    );
  }

  // ---------- Sub-section views: Partner Code / Bank Account / Expiry Watch ----------
  if (['partner-code', 'bank-account', 'expiry-watch'].includes(activeDocType)) {
    const tile = EXTRA_TILES.find((t) => t.id === activeDocType);
    const TileIcon = tile.icon;
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
        data-testid={`company-docs-${activeDocType}`}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => setActiveDocType(null)}
            className="text-[#71717A] hover:text-[#d4af37] flex items-center gap-1 text-sm"
            data-testid="back-to-hub"
          >
            <ArrowRight className={`h-4 w-4 ${language === 'ar' ? '' : 'rotate-180'}`} />
            {language === 'ar' ? 'رجوع لوثائق الشركة' : 'Back to Company Documents'}
          </button>
          <span className="text-[#71717A]">|</span>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <TileIcon className="h-5 w-5" style={{ color: tile.color }} />
            {language === 'ar' ? tile.titleAr : tile.titleEn}
          </h2>
        </div>

        {activeDocType === 'partner-code' && (
          <CompanySettingsSection language={language} mode="partner-code" />
        )}
        {activeDocType === 'bank-account' && (
          <BankAccountsSection language={language} />
        )}
        {activeDocType === 'expiry-watch' && (
          <ExpiryWatchSection language={language} />
        )}
      </motion.div>
    );
  }

  // ---------- Detail view (a single doc type) ----------
  const meta = DOC_TYPES[activeDocType];
  const Icon = meta.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid={`company-docs-${activeDocType}`}
    >
      <div className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setActiveDocType(null); fetchCounts(); }}
              className="text-[#71717A] hover:text-[#d4af37] flex items-center gap-1 text-sm"
              data-testid="back-to-hub"
            >
              <ArrowRight className={`h-4 w-4 ${language === 'ar' ? '' : 'rotate-180'}`} />
              {language === 'ar' ? 'رجوع' : 'Back'}
            </button>
            <span className="text-[#71717A]">|</span>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Icon className="h-5 w-5" style={{ color: meta.color }} />
              {language === 'ar' ? meta.titleAr : meta.titleEn}
            </h2>
          </div>
          <Button
            onClick={() => {
              // iter-249 — Enforce single-file constraint for tax_certificate.
              // If one already exists, ask the operator to confirm replacement.
              if (activeDocType === 'tax_certificate' && docs.length > 0) {
                if (!window.confirm(language === 'ar'
                  ? 'يوجد شهادة ضريبية سارية بالفعل. رفع شهادة جديدة سيستبدل السابقة (لن تظهر على الفواتير بعد الآن). هل تريد المتابعة؟'
                  : 'A tax certificate already exists. Uploading a new one will replace the previous one (it will no longer appear on invoices). Continue?')) {
                  return;
                }
              }
              setShowUpload(true);
            }}
            className="bg-[#d4af37] hover:bg-[#c9a227] text-black"
            data-testid="upload-new-btn"
          >
            <Plus className="h-4 w-4 me-1" />
            {language === 'ar' ? 'رفع وثيقة جديدة' : 'Upload new'}
          </Button>
        </div>

        {/* iter-277 (Feb 2026) — Tax Certificate: TRN is now auto-synced
           from the uploaded certificate by the AI. No manual input, no
           save button. Delete-the-certificate → TRN clears. Re-upload →
           TRN is replaced. This card shows the CURRENT active TRN + a
           short hint so operators understand where it came from. */}
        {activeDocType === 'tax_certificate' && (
          <div className="mb-5 p-4 rounded-2xl border border-emerald-500/20 bg-emerald-500/5"
               data-testid="auto-trn-card">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-4 w-4 text-emerald-400" />
              <h3 className="text-sm font-bold text-white">
                {language === 'ar' ? 'الرقم الضريبي (TRN) المُعتمد للفواتير' : 'Company TRN (used on all invoices)'}
              </h3>
            </div>
            <p className="text-[11px] text-white/50 mb-3">
              {language === 'ar'
                ? 'يُقرأ رقم TRN تلقائياً من الشهادة الضريبية المرفوعة أدناه. حذف الشهادة يمسح الرقم، ورفع شهادة جديدة يحلّ محلّها. يظهر على كل إيصال وفاتورة (عملاء + تجار + تأسيس + مهام يدوية).'
                : 'The TRN is read automatically from the tax certificate you upload below. Deleting the certificate clears it; uploading a new one replaces it. It appears on every invoice/receipt (customers + merchants + formation + manual tasks).'}
            </p>
            <div className="flex items-center gap-2 flex-wrap" dir="ltr">
              <span className="text-[10px] uppercase text-emerald-300/70 tracking-wider">Active TRN</span>
              <span
                className="px-3 py-1.5 rounded-md bg-black/40 border border-emerald-500/30 font-mono tracking-wider text-emerald-200 text-sm"
                data-testid="auto-trn-effective"
              >
                {effectiveTrn || (language === 'ar' ? '— (ارفع الشهادة الضريبية)' : '— (upload the tax certificate)')}
              </span>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#d4af37] mx-auto" /></div>
        ) : docs.length === 0 ? (
          <div className="text-center py-16 text-[#808080]" data-testid="empty-state">
            <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="mb-4">{language === 'ar' ? 'لا توجد وثائق مرفوعة بعد' : 'No documents uploaded yet'}</p>
            <Button onClick={() => setShowUpload(true)} className="bg-[#d4af37] hover:bg-[#c9a227] text-black">
              <Upload className="h-4 w-4 me-1" />
              {language === 'ar' ? 'ارفع أول وثيقة' : 'Upload first document'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {docs.map((doc) => {
              const data = doc.data || {};
              const primaryName = data.trade_name_ar || data.trade_name_en || data.legal_name_ar || data.legal_name_en || doc.filename;
              const idLabel = data.license_number || data.trn_number || '—';
              // Activities/business activities — used to distinguish licenses with similar names
              const activitiesList = Array.isArray(data.activities)
                ? data.activities.filter((a) => a && String(a).trim())
                : [];
              return (
                <div
                  key={doc.id}
                  className="p-4 rounded-xl border border-white/10 bg-[#0a0a0a] hover:border-[#d4af37]/40 transition-colors"
                  data-testid={`doc-row-${doc.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-[#d4af37]/10 flex items-center justify-center shrink-0">
                        {doc.content_type === 'application/pdf'
                          ? <FileText className="h-5 w-5 text-[#d4af37]" />
                          : <FileImage className="h-5 w-5 text-[#d4af37]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium truncate">{primaryName}</span>
                          <ExpiryBadge ddmmyyyy={data.expiry_date} language={language} />
                        </div>
                        <div className="text-xs text-[#808080] mt-1 flex flex-wrap gap-3">
                          <span>{idLabel}</span>
                          {data.issuing_authority && <span>{data.issuing_authority}</span>}
                          {data.tax_type && <span>{data.tax_type}</span>}
                          {data.expiry_date && <span>{language === 'ar' ? 'ينتهي:' : 'expires:'} {data.expiry_date}</span>}
                        </div>
                        {/* Activity types row — shown for trade licenses to distinguish similar names */}
                        {activitiesList.length > 0 && (
                          <div
                            className="mt-2 flex flex-wrap items-center gap-1.5"
                            data-testid={`doc-activities-${doc.id}`}
                          >
                            <span className="text-[10px] text-[#71717A] font-bold">
                              {language === 'ar' ? 'الأنشطة:' : 'Activities:'}
                            </span>
                            {activitiesList.slice(0, 4).map((act, i) => (
                              <span
                                key={i}
                                className="inline-block px-2 py-0.5 rounded-md text-[10px] font-medium border"
                                style={{
                                  backgroundColor: 'rgba(212, 175, 55, 0.10)',
                                  borderColor: 'rgba(212, 175, 55, 0.30)',
                                  color: '#d4af37',
                                }}
                                title={act}
                              >
                                {String(act).length > 40 ? `${String(act).slice(0, 40)}…` : act}
                              </span>
                            ))}
                            {activitiesList.length > 4 && (
                              <span className="text-[10px] text-[#71717A]">
                                +{activitiesList.length - 4}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => setViewDoc(doc)} className="h-8 border-white/10 text-white hover:bg-white/5" data-testid={`view-doc-${doc.id}`}>
                        <Eye className="h-3.5 w-3.5 me-1" />
                        {language === 'ar' ? 'عرض' : 'View'}
                      </Button>
                      <Button size="sm" variant="outline" onClick={async () => {
                        if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من الحذف؟' : 'Delete this document?')) return;
                        try {
                          await axios.delete(`${BACKEND_URL}/api/admin/company-docs/${doc.id}`, { headers: getAuthHeaders() });
                          // iter-277 — deleting the tax certificate MUST
                          // clear the active TRN so invoices don't keep
                          // stamping a number that is no longer proven.
                          if (activeDocType === 'tax_certificate') {
                            try {
                              await axios.put(
                                `${BACKEND_URL}/api/admin/company-settings/trn`,
                                { trn: '' },
                                { headers: getAuthHeaders() },
                              );
                              setEffectiveTrn('');
                            } catch (_) { /* non-fatal */ }
                          }
                          toast.success(language === 'ar' ? 'تم الحذف' : 'Deleted');
                          fetchDocs(activeDocType); fetchCounts();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed');
                        }
                      }} className="h-8 border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`del-doc-${doc.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showUpload && (
          <UploadModal
            docType={activeDocType}
            language={language}
            onClose={() => setShowUpload(false)}
            onSaved={() => { setShowUpload(false); fetchDocs(activeDocType); fetchCounts(); }}
          />
        )}
        {viewDoc && (
          <DocDetailModal
            doc={viewDoc}
            language={language}
            onClose={() => setViewDoc(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ====================================================================
// Upload + AI extraction modal
// ====================================================================
function UploadModal({ docType, language, onClose, onSaved }) {
  const meta = DOC_TYPES[docType];
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [notes, setNotes] = useState('');

  const handleFile = (f) => {
    if (!f) return;
    const ok = f.type === 'application/pdf' || f.type.startsWith('image/');
    if (!ok) { toast.error(language === 'ar' ? 'النوع غير مدعوم — PDF أو صورة فقط' : 'Only PDF/image allowed'); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error(language === 'ar' ? 'الحجم > 15MB' : 'File > 15MB'); return; }
    setFile(f);
    setExtracted(null);
    setFileId(null);
  };

  const handleExtract = async () => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/company-docs/extract`,
        fd,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } },
      );
      setExtracted(res.data?.data || {});
      setFileId(res.data?.file_id);
      toast.success(language === 'ar' ? 'تم استخراج البيانات' : 'Data extracted');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || (language === 'ar' ? 'فشل التعرف بالذكاء الاصطناعي' : 'AI extraction failed'));
    } finally {
      setExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!extracted || !fileId) return;
    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/company-docs`,
        { doc_type: docType, data: extracted, file_id: fileId, notes },
        { headers: getAuthHeaders() },
      );
      // iter-277 (Feb 2026) — Tax Certificate: auto-sync TRN from what the
      // AI extracted. No manual button, no save-again — the operator just
      // uploads the certificate and the TRN lands on every invoice.
      if (docType === 'tax_certificate') {
        const trn = (
          extracted.trn_number ||
          extracted.trn ||
          extracted.tax_registration_number ||
          ''
        ).toString().trim();
        if (trn) {
          try {
            await axios.put(
              `${BACKEND_URL}/api/admin/company-settings/trn`,
              { trn },
              { headers: getAuthHeaders() },
            );
          } catch (e) {
            // Non-fatal: doc still saved. Surface as info toast so operator sees it.
            toast.info(language === 'ar'
              ? 'تم حفظ الشهادة، لكن مزامنة TRN تلقائياً فشلت — سيُحاول مرة أخرى.'
              : 'Certificate saved but auto TRN sync failed — will retry on next open.');
          }
        }
      }
      toast.success(language === 'ar' ? 'تم حفظ الوثيقة' : 'Document saved');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (key, val) => setExtracted((prev) => ({ ...prev, [key]: val }));
  const Icon = meta.icon;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="upload-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" style={{ color: meta.color }} />
            {language === 'ar' ? `رفع ${meta.titleAr}` : `Upload ${meta.titleEn}`}
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            {language === 'ar' ? meta.descAr : meta.descEn}
          </DialogDescription>
        </DialogHeader>

        {/* File picker */}
        {!extracted && (
          <div
            className="border-2 border-dashed border-[#2a2a4e] rounded-2xl p-6 text-center hover:border-[#d4af37]/40 transition-colors"
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
          >
            <input
              type="file"
              accept=".pdf,image/*"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              id={`file-input-${docType}`}
              data-testid="file-input"
            />
            <label htmlFor={`file-input-${docType}`} className="cursor-pointer block">
              <Upload className="h-10 w-10 text-[#d4af37] mx-auto mb-3" />
              {file ? (
                <div>
                  <p className="text-white font-medium">{file.name}</p>
                  <p className="text-xs text-[#808080] mt-1">{(file.size / 1024).toFixed(1)} KB · {file.type}</p>
                </div>
              ) : (
                <>
                  <p className="text-white font-medium">{language === 'ar' ? 'اسحب الملف هنا أو اضغط للاختيار' : 'Drag file here or click to choose'}</p>
                  <p className="text-xs text-[#808080] mt-1">PDF / PNG / JPG · max 15MB</p>
                </>
              )}
            </label>
          </div>
        )}

        {/* Extract button */}
        {file && !extracted && (
          <Button
            onClick={handleExtract}
            disabled={extracting}
            className="w-full bg-[#d4af37] hover:bg-[#c9a227] text-black mt-4"
            data-testid="extract-ai-btn"
          >
            {extracting ? (
              <><Loader2 className="h-4 w-4 animate-spin me-2" />{language === 'ar' ? 'الذكاء الاصطناعي يقرأ الملف...' : 'AI is reading the file...'}</>
            ) : (
              <><Sparkles className="h-4 w-4 me-2" />{language === 'ar' ? 'استخراج البيانات بالذكاء الاصطناعي' : 'Extract data with AI'}</>
            )}
          </Button>
        )}

        {/* Extracted fields */}
        {extracted && (
          <div className="space-y-3 mt-2" data-testid="extracted-fields">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
              <Sparkles className="h-4 w-4" />
              {language === 'ar' ? 'تم استخراج البيانات بنجاح. راجعها قبل الحفظ.' : 'Data extracted. Review before saving.'}
            </div>
            {meta.fields.map((f) => (
              <div key={f.key}>
                <label className="block text-xs text-[#A1A1AA] mb-1">{language === 'ar' ? f.labelAr : f.labelEn}{f.highlight && ' ⏰'}</label>
                <Input
                  value={extracted[f.key] ?? ''}
                  onChange={(e) => updateField(f.key, e.target.value)}
                  className={`bg-[#0a0a0a] border-[#2a2a4e] text-white ${f.highlight ? 'border-amber-500/40' : ''}`}
                  data-testid={`field-${f.key}`}
                />
              </div>
            ))}
            <div>
              <label className="block text-xs text-[#A1A1AA] mb-1">{language === 'ar' ? 'ملاحظات' : 'Notes'}</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                data-testid="notes-field"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white">
            <X className="h-4 w-4 me-1" />
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          {extracted && (
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="save-doc-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileCheck2 className="h-4 w-4 me-1" />{language === 'ar' ? 'حفظ' : 'Save'}</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====================================================================
// Detail view modal
// ====================================================================
function DocDetailModal({ doc, language, onClose }) {
  const meta = DOC_TYPES[doc.doc_type];
  const Icon = meta?.icon || FileText;
  const fileUrl = getFileUrl(doc.file_id);
  const isPdf = doc.content_type === 'application/pdf';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="view-doc-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" style={{ color: meta?.color || '#d4af37' }} />
            {language === 'ar' ? meta?.titleAr : meta?.titleEn}
            {doc.data?.expiry_date && <ExpiryBadge ddmmyyyy={doc.data.expiry_date} language={language} />}
          </DialogTitle>
        </DialogHeader>

        {/* Preview */}
        <div className="rounded-xl overflow-hidden bg-black border border-white/5">
          {isPdf ? (
            <iframe
              src={fileUrl}
              title="doc"
              className="w-full h-[400px] bg-white"
              data-testid="doc-pdf-preview"
            />
          ) : (
            <img src={fileUrl} alt="doc" className="w-full max-h-[400px] object-contain" data-testid="doc-image-preview" />
          )}
        </div>

        {/* Extracted data */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {meta?.fields.map((f) => {
            const v = doc.data?.[f.key];
            if (v == null || v === '') return null;
            return (
              <div key={f.key} className="p-3 rounded-lg bg-[#0a0a0a] border border-white/5">
                <p className="text-[10px] text-[#808080] uppercase tracking-wide">{language === 'ar' ? f.labelAr : f.labelEn}</p>
                <p className="text-sm text-white mt-1 break-words">{Array.isArray(v) ? v.join(', ') : String(v)}</p>
              </div>
            );
          })}
        </div>

        {/* Activities (contained inside the view card) — shown when the extracted data has a non-empty activities list */}
        {Array.isArray(doc.data?.activities) && doc.data.activities.filter(Boolean).length > 0 && (
          <div className="p-4 rounded-lg bg-[#0a0a0a] border border-[#d4af37]/25" data-testid="view-doc-activities">
            <p className="text-[10px] text-[#d4af37] uppercase tracking-wide font-bold mb-2 flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {language === 'ar' ? 'الأنشطة التجارية' : 'Business Activities'}
              <span className="ms-1 text-[#71717A] font-normal">
                ({doc.data.activities.filter(Boolean).length})
              </span>
            </p>
            <div className="flex flex-wrap gap-1.5 max-h-56 overflow-y-auto">
              {doc.data.activities.filter(Boolean).map((act, i) => (
                <span
                  key={i}
                  className="inline-block px-2 py-1 rounded-md text-xs font-medium border"
                  style={{
                    backgroundColor: 'rgba(212,175,55,0.10)',
                    borderColor: 'rgba(212,175,55,0.30)',
                    color: '#d4af37',
                  }}
                >
                  {String(act)}
                </span>
              ))}
            </div>
          </div>
        )}

        {doc.notes && (
          <div className="p-3 rounded-lg bg-[#0a0a0a] border border-white/5">
            <p className="text-[10px] text-[#808080] uppercase tracking-wide flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {language === 'ar' ? 'ملاحظات' : 'Notes'}
            </p>
            <p className="text-sm text-white mt-1 whitespace-pre-wrap">{doc.notes}</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white">
            {language === 'ar' ? 'إغلاق' : 'Close'}
          </Button>
          <a href={fileUrl} target="_blank" rel="noreferrer">
            <Button className="bg-[#d4af37] hover:bg-[#c9a227] text-black">
              {language === 'ar' ? 'فتح في تبويب جديد' : 'Open in new tab'}
            </Button>
          </a>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
